/**
 * Whether the markup in `#root` can be hydrated for the address being shown. Dependency-free, because the
 * entry chunk (`main.tsx`) needs the answer to decide *when* to start the app, long before React is loaded
 * (it then tells `boot.tsx` *how*, see `bootClient`). Nothing in this file may be shared with the app's own
 * modules: the bundler would pull a shared module into the app's chunk and the entry would have to load that
 * chunk at once (`vite.config.ts`).
 */

/**
 * Attribute on `#root` naming the address the markup was rendered for: `PRERENDER_ATTRIBUTE` in
 * `prerender/markers.ts`, repeated here to keep this file import-free; the `canHydrate` tests in `boot.test.tsx`
 * build their roots with the marker constant, so they fail if the two ever differ.
 */
const PRERENDER_ATTRIBUTE = 'data-prerender';

/** Search parameters that never change what a page shows (campaign tags, click ids). */
const NEUTRAL_PARAMS = /^(utm_[a-z0-9_]+|fbclid|gclid|msclkid|dclid|mc_[a-z]+|_ga|_gl)$/i;

const trimSlash = (path: string): string => (path.length > 1 ? path.replace(/\/+$/, '') : path);

/**
 * Is the markup in `container` the pre-rendered page for this very address? Only then can React
 * hydrate it. A different address (the shell served for a route that was not pre-rendered) or a
 * search string that changes the page (`/shop?color=black`, `/contact-us?product=…`) must be rendered
 * afresh — hydrating would find the wrong content. Campaign parameters do not count.
 */
export function canHydrate(
  container: Element,
  location: { pathname: string; search: string },
): boolean {
  const rendered = container.getAttribute(PRERENDER_ATTRIBUTE);
  if (rendered === null || !container.hasChildNodes()) return false;
  if (trimSlash(rendered) !== trimSlash(location.pathname)) return false;
  for (const key of new URLSearchParams(location.search).keys()) {
    if (!NEUTRAL_PARAMS.test(key)) return false;
  }
  return true;
}
